/**
 * Pure status/mode derivation for the meeting-detail route.
 *
 * Extracted so the record/observe/summary/finalize gates can be unit-tested
 * without mounting the TanStack route. Inputs are plain values — sessionStorage
 * (`lexicanon:activeRecording`) is read by the route and passed in as
 * `isThisTabRecording`.
 */
import { isLiteTemplate, LIVE_MEETING_STATUSES, MEETING_STATUSES, RECORDING_MEETING_STATUSES } from "@lexicanon/shared"
import type { TProcessingStage } from "../../../../lib/pipeline-progress"

export interface IMeetingModeFields {
  source?: string
  status: string
  analysis?: unknown
  recording_url?: string | null
  template_id?: string | null
}

/** Lite meetings skip analysis and finish at `summarized` with transcript + speakers only. */
export function isLiteMeeting(meeting: Pick<IMeetingModeFields, "template_id">): boolean {
  return isLiteTemplate(meeting.template_id)
}

export function isIntermediateStatus(status: string): boolean {
  return LIVE_MEETING_STATUSES.has(status)
}

/**
 * True when THIS tab should mount `<RecordingControls>`. `recovering` is the
 * backend finalizing via the recovery path — never a recordable state. For
 * `in_progress`, only the tab that stashed `lexicanon:activeRecording` is the
 * recorder; every other tab is an observer.
 */
export function isActivelyRecording(
  meeting: Pick<IMeetingModeFields, "source" | "status">,
  isThisTabRecording: boolean,
): boolean {
  return (
    meeting.source === "browser" &&
    RECORDING_MEETING_STATUSES.has(meeting.status) &&
    meeting.status !== MEETING_STATUSES.RECOVERING &&
    (meeting.status !== "in_progress" || isThisTabRecording)
  )
}

/** Watching an in-progress browser meeting from a tab that is not recording. */
export function isObserverMeeting(
  meeting: Pick<IMeetingModeFields, "source" | "status">,
  isThisTabRecording: boolean,
): boolean {
  return meeting.source === "browser" && meeting.status === MEETING_STATUSES.IN_PROGRESS && !isThisTabRecording
}

/** Lexicanon post-recording summary surface (analysis landed, meeting not live). */
export function shouldUseLexicanonSummary(args: {
  product: string
  isActivelyRecording: boolean
  isIntermediate: boolean
  hasAnalysis: boolean
  hasDisplayAnalysis: boolean
  isLite?: boolean
}): boolean {
  return (
    args.product === "lexicanon" &&
    !args.isLite &&
    !args.isActivelyRecording &&
    !args.isIntermediate &&
    args.hasAnalysis &&
    args.hasDisplayAnalysis
  )
}

export function isFinalizingLexMeeting(args: {
  product: string
  isActivelyRecording: boolean
  useLexicanonSummary: boolean
  status: string
  analysis: unknown
  recordingUrl?: string | null
  isIntermediate: boolean
  isLite?: boolean
}): boolean {
  // A finished lite meeting is `summarized` with no analysis — that's the
  // product, not a clobbered full meeting. Don't hold the finalize screen
  // and don't treat it as the "terminal without analysis" error path.
  if (args.isLite && args.status === MEETING_STATUSES.SUMMARIZED) return false

  const terminalWithoutAnalysis =
    !args.isLite &&
    !args.analysis &&
    (args.status === MEETING_STATUSES.SUMMARIZED || args.status === MEETING_STATUSES.ERROR)
  return (
    args.product === "lexicanon" &&
    !args.isActivelyRecording &&
    !args.useLexicanonSummary &&
    args.status !== MEETING_STATUSES.ERROR &&
    !terminalWithoutAnalysis &&
    (args.isIntermediate || (!!args.recordingUrl && !args.analysis))
  )
}

export function shouldShowFinalizing(args: {
  isFinalizingLex: boolean
  useLexicanonSummary: boolean
  justFinalized: boolean
  showSummary: boolean
}): boolean {
  return args.isFinalizingLex || (args.useLexicanonSummary && args.justFinalized && !args.showSummary)
}

/**
 * Stage/progress shown on the finalize screen.
 *
 * Full waits for the analysis. "Done / 100%" only once that blob has landed;
 * until then a premature `complete` stays on the summary step and the number
 * stays at or under 95, so the card cannot freeze on Done.
 *
 * Lite has no analysis (ADR 0045). It reaches Done when the meeting is
 * `summarized`, or when the backend stage is already `complete`. A Lite
 * "analyzing" report is the title step (`lite-finalize` emits it while
 * choosing a title) and is shown as that step. Progress stays under 100
 * until Done, so the old 95% stall does not return. Pass `isLite` — without
 * it this keeps Full's wait, so a caller that has not been updated does not
 * flip Lite meetings early.
 */
export function deriveFinishingDisplay(args: {
  analysisReady: boolean
  effectiveStage: TProcessingStage | null
  isIntermediate: boolean
  effectiveProgress: number
  isLite?: boolean
  /** Meeting status. Lite reaches Done on `summarized`. */
  status?: string
}): { finishingStage: TProcessingStage | null; finishingProgress: number } {
  if (args.isLite) {
    const done = args.status === MEETING_STATUSES.SUMMARIZED || args.effectiveStage === "complete"
    if (done) return { finishingStage: "complete", finishingProgress: 100 }
    const raw = Number.isFinite(args.effectiveProgress) ? args.effectiveProgress : 0
    return {
      finishingStage: args.effectiveStage,
      finishingProgress: Math.min(99, Math.max(0, raw)),
    }
  }

  const finishingStage: TProcessingStage | null = args.analysisReady
    ? "complete"
    : args.effectiveStage === "complete" || !args.isIntermediate
      ? "analyzing"
      : args.effectiveStage
  return {
    finishingStage,
    finishingProgress: args.analysisReady ? 100 : Math.min(args.effectiveProgress, 95),
  }
}
